import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Pencil, Trash2, KeyRound, UserCog, ShieldCheck, ShieldOff } from 'lucide-react';
import { useT } from '@/store/i18n';
import { useAuth } from '@/store/auth';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { confirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/components/ui/Toast';
import { api } from '@/lib/api';
import { AdminResetPasswordModal } from '@/components/AdminResetPasswordModal';
import type { User, Role } from '@/types';

interface UsersPayload { users?: User[]; }

const ROLE_TONE: Record<Role, 'brand' | 'info' | 'warning' | 'neutral'> = {
  admin: 'warning',
  academic_admin: 'info',
  teacher: 'brand',
  executive: 'neutral',
};

function avatarColor(seed: string) {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash << 5) - hash + seed.charCodeAt(i);
  const palette = ['bg-emerald-500','bg-blue-500','bg-violet-500','bg-amber-500','bg-rose-500','bg-cyan-500','bg-indigo-500','bg-pink-500'];
  return palette[Math.abs(hash) % palette.length];
}
function initials(name: string) {
  if (!name) return '?';
  const p = name.trim().split(/\s+/);
  return (p[0][0] + (p[1]?.[0] ?? '')).toUpperCase();
}

interface UserFormData {
  id?: number;
  username: string;
  full_name: string;
  email: string;
  role: Role;
  password: string;
}

const EMPTY: UserFormData = { username: '', full_name: '', email: '', role: 'teacher', password: '' };

export function Users() {
  const t = useT();
  const qc = useQueryClient();
  const currentUser = useAuth((s) => s.user);
  const isAdmin = currentUser?.role === 'admin';
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<Role | ''>('');
  const [editing, setEditing] = useState<UserFormData | null>(null);
  const [resetTarget, setResetTarget] = useState<User | null>(null);

  const list = useQuery({
    queryKey: ['users'],
    queryFn: () => api<{ data?: UsersPayload } & UsersPayload>('users/index.php').then((r) => r.data ?? r),
  });

  const filtered = useMemo(() => {
    const arr = list.data?.users ?? [];
    return arr.filter((u) => {
      if (roleFilter && u.role !== roleFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        return (
          u.username.toLowerCase().includes(s) ||
          u.full_name.toLowerCase().includes(s) ||
          u.email.toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [list.data?.users, search, roleFilter]);

  const save = useMutation({
    mutationFn: async (data: UserFormData) => {
      if (data.id) {
        return api(`users/manage.php?id=${data.id}`, {
          method: 'PUT',
          json: {
            username: data.username,
            full_name: data.full_name,
            email: data.email,
            role: data.role,
            ...(data.password ? { password: data.password } : {}),
          },
        });
      }
      return api('users/manage.php', {
        method: 'POST',
        json: data,
      });
    },
    onSuccess: () => {
      toast.success(editing?.id ? t('users.success_save') : t('users.success_add'));
      qc.invalidateQueries({ queryKey: ['users'] });
      setEditing(null);
    },
    onError: (e: Error) => toast.error(t('error.save_failed'), e.message),
  });

  const toggleStatus = useMutation({
    mutationFn: (id: number) => api(`users/toggle-status.php?id=${id}`, { method: 'POST' }),
    onSuccess: () => {
      toast.success(t('users.success_status'));
      qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (e: Error) => toast.error(t('error.save_failed'), e.message),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api(`users/manage.php?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success(t('users.success_delete'));
      qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (e: Error) => toast.error(t('error.delete_failed'), e.message),
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('users.title')}
        description={`${t('common.total')} ${(list.data?.users ?? []).length.toLocaleString('th-TH')} ${t('units.people')}`}
        actions={
          <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => setEditing({ ...EMPTY })}>
            {t('users.add')}
          </Button>
        }
      />

      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            leftIcon={<Search className="w-4 h-4" />}
            placeholder={t('users.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value as Role | '')}>
            <option value="">{t('filter.all_roles')}</option>
            {(Object.keys(ROLE_TONE) as Role[]).map((r) => (
              <option key={r} value={r}>{t(`role.${r}`)}</option>
            ))}
          </Select>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <th className="text-left font-semibold px-4 py-3">{t('users.col.user')}</th>
                <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">{t('users.col.email')}</th>
                <th className="text-left font-semibold px-4 py-3">{t('users.col.role')}</th>
                <th className="text-left font-semibold px-4 py-3 hidden lg:table-cell">{t('users.col.status')}</th>
                <th className="text-right font-semibold px-4 py-3">{t('users.col.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}><td colSpan={5} className="px-4 py-3"><Skeleton className="h-4 w-full" /></td></tr>
                ))
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5}><EmptyState title={t('users.empty')} /></td></tr>
              ) : (
                filtered.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50/70">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full text-white text-xs font-semibold flex items-center justify-center flex-shrink-0 ${avatarColor(u.username)}`}>
                          {initials(u.full_name || u.username)}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 truncate">{u.full_name}</div>
                          <div className="text-xs text-slate-500 truncate">@{u.username}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-slate-600">{u.email}</td>
                    <td className="px-4 py-3">
                      <Badge tone={ROLE_TONE[u.role]}>{t(`role.${u.role}`)}</Badge>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {u.is_active === false ? (
                        <Badge tone="danger" dot>{t('users.status_inactive')}</Badge>
                      ) : (
                        <Badge tone="success" dot>{t('users.status_active')}</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost" size="icon" aria-label={t('btn.edit')}
                          onClick={() => setEditing({
                            id: u.id,
                            username: u.username,
                            full_name: u.full_name,
                            email: u.email,
                            role: u.role,
                            password: '',
                          })}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {/* Admin-only: reset another user's password */}
                        {isAdmin && u.id !== currentUser?.id && (
                          <Button
                            variant="ghost" size="icon"
                            aria-label={t('pwd.reset.btn_aria')}
                            title={t('pwd.reset.btn_aria')}
                            onClick={() => setResetTarget(u)}
                          >
                            <KeyRound className="w-4 h-4 text-amber-500" />
                          </Button>
                        )}
                        <Button
                          variant="ghost" size="icon"
                          aria-label={u.is_active === false ? t('users.enable') : t('users.disable')}
                          onClick={async () => {
                            const ok = await confirmDialog({
                              title: (u.is_active === false ? t('users.enable') : t('users.disable')) + ' ' + t('users.delete_confirm') + '?',
                              confirmLabel: u.is_active === false ? t('users.enable') : t('users.disable'),
                            });
                            if (ok) toggleStatus.mutate(u.id);
                          }}
                        >
                          {u.is_active === false ? <ShieldCheck className="w-4 h-4" /> : <ShieldOff className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost" size="icon" aria-label={t('btn.delete')}
                          onClick={async () => {
                            const ok = await confirmDialog({
                              title: `${t('users.delete_confirm')} "${u.full_name}"?`,
                              description: t('users.delete_desc'),
                              destructive: true,
                              confirmLabel: t('btn.delete'),
                            });
                            if (ok) remove.mutate(u.id);
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Admin: reset user password */}
      <AdminResetPasswordModal
        open={!!resetTarget}
        onClose={() => setResetTarget(null)}
        targetUser={resetTarget}
      />

      {/* Edit/Create modal */}
      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? t('users.modal_edit') : t('users.modal_add')}
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>{t('btn.cancel')}</Button>
            <Button
              loading={save.isPending}
              onClick={() => editing && save.mutate(editing)}
              leftIcon={<UserCog className="w-4 h-4" />}
            >
              {t('btn.save')}
            </Button>
          </>
        }
      >
        {editing && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                label={t('users.field.username')}
                value={editing.username}
                onChange={(e) => setEditing({ ...editing, username: e.target.value })}
                required
              />
              <Input
                label={t('users.field.fullname')}
                value={editing.full_name}
                onChange={(e) => setEditing({ ...editing, full_name: e.target.value })}
                required
              />
            </div>
            <Input
              label={t('users.field.email')}
              type="email"
              value={editing.email}
              onChange={(e) => setEditing({ ...editing, email: e.target.value })}
              required
            />
            <Select
              label={t('users.field.role')}
              value={editing.role}
              onChange={(e) => setEditing({ ...editing, role: e.target.value as Role })}
            >
              {(Object.keys(ROLE_TONE) as Role[]).map((r) => (
                <option key={r} value={r}>{t(`role.${r}`)}</option>
              ))}
            </Select>
            <Input
              label={editing.id ? t('users.field.password_new') : t('users.field.password')}
              type="password"
              value={editing.password}
              onChange={(e) => setEditing({ ...editing, password: e.target.value })}
              leftIcon={<KeyRound className="w-4 h-4" />}
              required={!editing.id}
              hint={editing.id ? t('users.field.password_hint_new') : t('users.field.password_hint')}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
